import { useEffect, useMemo, useState } from 'react';
import UserService from '@/domain/Users/UserService';

export interface OrgSetting {
	key: string;
	value: string | number | null;
}

export interface OrgSettingGroup {
	groupKey?: string;
	groupName?: string;
	settings?: OrgSetting[] | Record<string, string>;
}

export interface OrgSettingsByGroup {
	contactSupport?: OrgSettingGroup;
	ContactSupport?: OrgSettingGroup;
	contactsupport?: OrgSettingGroup;
}

export type OrganizationSettings = OrgSettingGroup[] | OrgSettingsByGroup;

const userService = new UserService();

const reduceSettings = (settings: OrgSetting[]): Record<string, string> =>
	settings.reduce((acc: Record<string, string>, item) => {
		if (item?.key) {
			acc[item.key] = String(item?.value ?? '').trim();
		}
		return acc;
	}, {});

export function useOrganizationSettings(organizationId: string | number | undefined) {
	const [organizationSettings, setOrganizationSettings] = useState<OrganizationSettings | null>(() => {
		const raw = localStorage.getItem('organizationContactSettings');
		return raw ? JSON.parse(raw) : null;
	});

	useEffect(() => {
		if (!organizationId) return;
		let cancelled = false;

		userService
			.getUserSettingByOrganizationId(organizationId)
			.then((settings: OrganizationSettings | null) => {
				if (cancelled || !settings) return;
				setOrganizationSettings(settings);
				localStorage.setItem('organizationContactSettings', JSON.stringify(settings));
			})
			.catch((error: Error) => {
				if (!cancelled) {
					console.error('Error fetching organization user settings:', error);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [organizationId]);

	const contactSupportSettings = useMemo<Record<string, string>>(() => {
		if (!organizationSettings) return {};

		if (Array.isArray(organizationSettings)) {
			const contactSupportGroup = organizationSettings.find((item) => {
				const groupKey = String(item?.groupKey ?? '').toLowerCase();
				const groupName = String(item?.groupName ?? '').toLowerCase();
				return groupKey === 'contactsupport' || groupName === 'contactsupport';
			});

			if (!contactSupportGroup || !Array.isArray(contactSupportGroup.settings)) {
				return {};
			}

			return reduceSettings(contactSupportGroup.settings);
		}

		const contactSupportGroup =
			organizationSettings.contactSupport ??
			organizationSettings.ContactSupport ??
			organizationSettings.contactsupport ??
			null;

		if (!contactSupportGroup) return {};

		if (Array.isArray(contactSupportGroup.settings)) {
			return reduceSettings(contactSupportGroup.settings);
		}

		return contactSupportGroup.settings ?? {};
	}, [organizationSettings]);

	const clientSuccessManagerName = String(
		contactSupportSettings.successManagerName ??
			contactSupportSettings.clientSuccessManagerName ??
			'',
	).trim();

	const clientSuccessManagerEmail = String(
		contactSupportSettings.successManagerEmail ??
			contactSupportSettings.clientSuccessManagerEmail ??
			'',
	).trim();

	return { clientSuccessManagerName, clientSuccessManagerEmail };
}
